import type { MapPosterResponse } from '~/local/custom-map-puzzle/types';

export interface MapPosterRequest {
  lat: number;
  lon: number;
  city: string;
  country: string;
  display_city?: string;
  display_country?: string;
  theme: string;
  distance: number;
  font_family?: string;
  quality: string;
  /** A whole-city pick: centre on the listed city, whose map data is pre-fetched */
  snap?: boolean;
  /** A whole country or state (instead of `distance` around the point), with its options */
  area_type?: string;
  area_name?: string;
  country_code?: string;
  road_detail?: string;
  show_boundaries?: boolean;
  colour_states?: boolean;
  grid?: boolean;
  labels?: { text: string; lat: number; lon: number }[];
  show_cities?: boolean;
}

const POLL_INTERVAL_MS = 1500;
// The poster Lambda's own timeout is 5 minutes
const GIVE_UP_AFTER_MS = 5.5 * 60 * 1000;

const wait = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true }
    );
  });

/**
 * Generate a map poster preview: start a job on /api/map-poster, then poll it until the poster
 * is ready. A listed city's map data is cached, so that takes a few seconds; a place mapped
 * for the first time waits on OpenStreetMap and can take a minute or more.
 * Aborting `signal` stops polling with an AbortError.
 */
export async function requestMapPoster(
  params: MapPosterRequest,
  signal: AbortSignal
): Promise<MapPosterResponse> {
  const started = await fetch('/api/map-poster', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
    signal,
  });
  const { jobId, error }: { jobId?: string; error?: string } = await started.json();
  if (!jobId) {
    return { success: false, error };
  }

  const deadline = Date.now() + GIVE_UP_AFTER_MS;
  while (Date.now() < deadline) {
    await wait(POLL_INTERVAL_MS, signal);
    const response = await fetch(`/api/map-poster?job=${jobId}`, { signal });
    if (response.status !== 202) {
      return response.json();
    }
  }
  return { success: false, error: 'Map generation is taking too long. Please try again.' };
}
